import React, { useState, useEffect } from 'react';
import './LoginForm.css';
import Swal from 'sweetalert2';
import { authService } from '../api/auth';
import { EyeIcon, EyeSlashIcon, SparklesIcon } from '@heroicons/react/24/outline';

const LoginForm = ({ onLoginSuccess, onGoToRegister }) => {
  const [email, setEmail] = useState('');
  const [contraseña, setContraseña] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [demoLoaded, setDemoLoaded] = useState(false);

  useEffect(() => {
    // Detectar si ingresó con parámetro demo en la URL (?demo=1 o ?demo=true o #/?demo=1)
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = window.location.hash.includes('?')
      ? new URLSearchParams(window.location.hash.split('?')[1])
      : new URLSearchParams();

    const isDemo =
      searchParams.get('demo') === '1' ||
      searchParams.get('demo') === 'true' ||
      hashParams.get('demo') === '1' ||
      hashParams.get('demo') === 'true';

    const shouldAutoLogin =
      searchParams.get('autologin') === '1' ||
      searchParams.get('autologin') === 'true' ||
      hashParams.get('autologin') === '1' ||
      hashParams.get('autologin') === 'true';

    if (isDemo) {
      setEmail('usuarioprueba@optica.com');
      setContraseña('admin12345');
      setDemoLoaded(true);

      if (shouldAutoLogin) {
        setLoading(true);
        authService
          .login('usuarioprueba@optica.com', 'admin12345')
          .then((data) => {
            if (onLoginSuccess) {
              onLoginSuccess(data.usuario);
            }
          })
          .catch((err) => {
            console.error('Error en autologin demo:', err);
            setLoading(false);
          });
      }
    }
  }, [onLoginSuccess]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !contraseña) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos requeridos',
        text: 'Por favor completa email y contraseña'
      });
      return;
    }

    setLoading(true);
    try {
      const data = await authService.login(email.trim(), contraseña);

      Swal.fire({
        icon: 'success',
        title: '¡Bienvenido!',
        text: `Hola ${data.usuario.nombre_completo}`,
        timer: 1500,
        showConfirmButton: false
      });

      // Llamar callback
      if (onLoginSuccess) {
        onLoginSuccess(data.usuario);
      }

    } catch (error) {
      console.error('Error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al iniciar sesión',
        text: error.message
      });
    } finally {
      setContraseña('');
      setLoading(false);
    }
  };

  // Acceso Demo en 1 solo clic sin escribir credenciales
  const handleLoginDemo = async () => {
    const demoEmail = 'Usuarioprueba@optica.com';
    const demoPassword = 'admin12345';
    setEmail(demoEmail);
    setContraseña(demoPassword);
    setLoading(true);

    try {
      const data = await authService.login(demoEmail, demoPassword);

      Swal.fire({
        icon: 'success',
        title: '¡Bienvenido a la Demo!',
        text: `Hola ${data.usuario.nombre_completo}`,
        timer: 1500,
        showConfirmButton: false
      });

      if (onLoginSuccess) {
        onLoginSuccess(data.usuario);
      }
    } catch (error) {
      console.error('Error al ingresar a la demo:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al iniciar sesión demo',
        text: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h1>Wolf Eye</h1>
          <h1>Optical System</h1>
          <p>Sistema de Gestión</p>
        </div>

        {demoLoaded && (
          <div className="flex items-center gap-2 p-3 mb-4 rounded-xl bg-blue-50 border border-blue-200 text-blue-900 text-xs">
            <SparklesIcon className="w-4 h-4 text-blue-600 shrink-0 animate-spin" />
            <span>
              <strong>Modo de prueba activado:</strong> Credenciales precargadas. Presiona <strong>Iniciar Sesión</strong> para ingresar.
            </span>
          </div>
        )}

        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@optica.local"
              disabled={loading}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="contraseña">Contraseña</label>
            <div className="password-input">
              <input
                id="contraseña"
                type={showPassword ? 'text' : 'password'}
                value={contraseña}
                onChange={(e) => setContraseña(e.target.value)}
                placeholder="••••••••"
                disabled={loading}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              > 
                {showPassword ? <EyeSlashIcon className="w-6 h-6 text-gray-500" /> : <EyeIcon className="w-6 h-6 text-blue-500" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>

          {/* Botón de acceso demo en 1 solo clic */}
          <button
            type="button"
            onClick={handleLoginDemo}
            disabled={loading}
            className="demo-button"
          >
            <SparklesIcon style={{ width: '1.25rem', height: '1.25rem' }} />
            <span>{loading ? 'Accediendo a la demo...' : 'Probar Demo en 1 Clic'}</span>
          </button>

          <div className="divider">
            <span>¿No tienes cuenta?</span>
          </div>

          <button
            type="button"
            className="register-button"
            onClick={() => onGoToRegister && onGoToRegister()}
            disabled={loading}
          >
            Registrarse
          </button>
        </form>
      </div>

      <div className="login-background">
        <div className="gradient-blob blob-1"></div>
        <div className="gradient-blob blob-2"></div>
        <div className="gradient-blob blob-3"></div>
      </div>
    </div>
  );
};

export default LoginForm;
